import React from 'react';

const SLA_LABELS = { 0: 'Basic (95%)', 1: 'Professional (99%)', 2: 'Enterprise (99.9%)', 'Basic': 'Basic (95%)', 'Professional': 'Professional (99%)', 'Enterprise': 'Enterprise (99.9%)' };

function formatPrice(v) { return typeof v === 'number' && v > 1 ? `$${(v/1_000_000).toFixed(4)}` : `$${v}`; }
function formatStake(v) { return typeof v === 'number' && v > 1_000_000 ? `${(v/1_000_000_000).toLocaleString()} $THANE` : `${(v||0).toLocaleString()} $THANE`; }

export default function ProviderDetail({ provider, onClose }) {
  if (!provider) return null;
  const p = provider;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">{p.name}</h2>
            <p className="text-xs text-slate-400 mt-1 font-mono">{p.wallet_full || p.wallet || p.provider}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">&times;</button>
        </div>

        {/* Reputation */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Active Reputation (90d)</p>
            <p className={`text-3xl font-bold mt-1 ${
              p.reputation_active >= 80 ? 'text-emerald-400' :
              p.reputation_active >= 60 ? 'text-blue-400' : 'text-yellow-400'
            }`}>
              {p.reputation_active}<span className="text-lg text-slate-500">/100</span>
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Lifetime Reputation</p>
            <p className="text-3xl font-bold text-slate-300 mt-1">
              {p.reputation_lifetime}<span className="text-lg text-slate-500">/100</span>
            </p>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <DetailCard label="Price/Call" value={formatPrice(p.price_per_call)} />
          <DetailCard label="Total Calls" value={(p.total_calls || 0).toLocaleString()} />
          <DetailCard label="SLA Tier" value={SLA_LABELS[p.sla_tier ?? p.sla_name ?? 0] || 'Basic'} />
          <DetailCard label="Max Latency" value={`${p.promised_max_latency_ms || 5000}ms`} />
          <DetailCard label="Staked" value={formatStake(p.stake_amount)} />
          <DetailCard label="Free Tier" value={p.free_tier_daily > 0 ? `${p.free_tier_daily}/day` : 'None'} />
          <DetailCard label="Registered" value={p.registered_at?.split('T')[0] || 'N/A'} />
          <DetailCard label="Status" value={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'text-emerald-400' : 'text-red-400'} />
        </div>

        {/* Tags */}
        <div className="mb-6">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Capability Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {(p.tags || []).map((tag, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Endpoint */}
        <div className="mb-6">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">MCP Endpoint</p>
          <code className="text-xs text-blue-400 bg-slate-800 px-3 py-2 rounded-lg block font-mono break-all">
            {p.endpoint}
          </code>
        </div>

        {/* Bulk Discounts */}
        {p.bulk_discounts && p.bulk_discounts.length > 0 && (
          <div className="mb-6">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Bulk Discounts</p>
            <div className="flex gap-2">
              {p.bulk_discounts.map(([minCalls, discount], i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                  {minCalls}+ calls: {discount}% off
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Connect Button */}
        <div className="flex gap-3">
          <button className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 rounded-xl text-sm font-semibold transition-colors">
            Connect Agent
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailCard({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-slate-800/30 rounded-lg p-3">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-medium mt-0.5 ${color}`}>{value}</p>
    </div>
  );
}
